import axios from "axios";
import {
  convertSecondsToHHMM,
  formatAlias,
  getCluster,
  getDateInMMDDYYYYWithOffset,
  getPickTimeToBeDisplayed,
  isARorSSD,
  getTimeDiffInSecondsFromCurrentTimeInHHMMSSFormat,
  convertMillisToSecond,
  isTimeValid,
  isRSWSBosonFlowEnabled,
} from "../util/Util";
import { getStationCode } from "../../network/NetworkUtil";
import {
  isValid,
  logOperationalMetric,
  publishCountToCSM,
} from "../../overview/utils/utils";
import {
  ASSIGNABLE,
  getAmxlRouteStatus,
  getOverallRouteStatus,
  getPickStatusForPicklist,
  handleUnassignedRoutes,
  isPartial,
} from "../RouteStatusManager";
import { AR_V2, DEFAULT_CAPABILITIES, NOT_APPLICABLE } from "../util/Constants";
import {
  getRouteStagingLocation,
  transformStagingLocations,
} from "../util/DataTransformUtil";
import * as SSDConstants from "../../outboundSSD/util/Constants";
import {
  AMXL_ROUTE_TYPES,
  AMXL_SHIPMENT_TYPES,
  TIMEOUTS,
} from "../../util/Constants";
import { getAPIGatewayDataFromParent } from "../../handler/ParentDataHandler";
import { getAmxlRouteType } from "../../util/Util";

const METRIC_KEY = "SCC_OUTBOUND_AMZL_OVERVIEW";
const ROUTE_INFO_METRIC = "getRouteInformation";
const AMXL_ROUTE_INFO_METRIC_SVS = "getAmxlRouteInformation.SVS";
const AMXL_ROUTE_INFO_METRIC_RSWS = "getAmxlRouteInformation.RSWS";
const DWP_METADATA_METRIC = "getDWPMetadata";
const metricProcessName = `${getStationCode()}_${METRIC_KEY}`;

export function fetchRouteInformation(args) {
  args.stationConfig.amxl
    ? getAmxlRouteInformation(args)
    : getRouteInformation(args);
}

async function getAmxlRouteInformation(args, capability) {
  const {
    dispatcher,
    filterDispatcher,
    baseUrl,
    xptNodes,
    stationConfig,
    configDispatch,
  } = args;
  const startTime = Date.now();
  try {
    const stationCode = getStationCode();
    const requestBody = {
      filters: {
        NODE: [stationCode],
      },
      fieldsRequired: ["ID", "CODE", "PLANNED_COUNT", "CPT"],
    };
    const request = getStowRouteAPIGatewayRequest(requestBody);
    const response = await getAPIGatewayDataFromParent(request);
    if (response.status === 200) {
      logOperationalMetric(
        startTime,
        metricProcessName,
        AMXL_ROUTE_INFO_METRIC_SVS,
        true,
      );
      const routeIdToRouteMap = response.data.routes.reduce(
        (a, v) => ({ ...a, [v.id]: v }),
        {},
      );
      const routeIds = Object.keys(routeIdToRouteMap);
      let routesNotAssignedCount = 0;
      const enrichedRouteInfo = await getEnrichedAmxlRouteInformationBatch(
        routeIds,
        baseUrl,
        stationCode,
        stationConfig,
      );
      const routeIdToEnrichDataMap = enrichedRouteInfo
        ? enrichedRouteInfo.routes.reduce(
            (a, v) => ({ ...a, [v.routeId]: v }),
            {},
          )
        : null;
      for (const routeId of routeIds) {
        routeIdToRouteMap[routeId].routeCode = routeIdToRouteMap[routeId].code;
        routeIdToRouteMap[routeId].routeId = routeIdToRouteMap[routeId].id;
        routeIdToRouteMap[routeId].stationCode = stationCode;
        routeIdToRouteMap[routeId].type = getAmxlRouteType(
          routeIdToRouteMap[routeId].code,
        );
        routeIdToRouteMap[routeId].criticalPullTime =
          routeIdToRouteMap[routeId].cpt;

        if (routeIdToEnrichDataMap && routeId in routeIdToEnrichDataMap) {
          routeIdToRouteMap[routeId].departProgress =
            routeIdToEnrichDataMap[routeId].departProgress;
          routeIdToRouteMap[routeId].stowProgress =
            routeIdToEnrichDataMap[routeId].stowProgress;
          routeIdToRouteMap[routeId].routeStatus = getAmxlRouteStatus(
            routeIdToEnrichDataMap[routeId],
          );
          routeIdToRouteMap[routeId].driver =
            routeIdToEnrichDataMap[routeId].driver;
          routeIdToRouteMap[routeId].stagingLocation =
            routeIdToEnrichDataMap[routeId].stagingLocation;
          routeIdToRouteMap[routeId].stagingLocations =
            routeIdToEnrichDataMap[routeId].stagingLocations;
          if (routeIdToEnrichDataMap[routeId].activeAssignee == null) {
            routesNotAssignedCount++;
          }
        }
      }
      const amxlRoutes = Object.values(routeIdToRouteMap);
      response.data.routes = amxlRoutes;
      const lastUpdated = new Date().getTime();
      dispatcher({
        type: "UPDATE_ROUTE_DATA_CACHE",
        data: {
          response: response.data,
          xptNodes: xptNodes,
          stationCode: stationCode,
        },
        lastUpdated: lastUpdated,
      });
      dispatcher({
        type: "RECEIVE_DATA",
        data: getRouteData(amxlRoutes, capability, stationConfig),
        routesNotAssignedCount: routesNotAssignedCount,
        lastUpdated: lastUpdated,
      });
      filterDispatcher({
        type: "RECEIVE_FILTER_DATA",
        data: getFilterData(amxlRoutes, stationConfig),
      });
      configDispatch &&
        configDispatch({
          type: "RECEIVE_REFRESH_INTERVAL",
          data: {
            refreshInterval: enrichedRouteInfo.routeRefreshRate,
          },
        });
    }
  } catch (error) {
    logOperationalMetric(
      startTime,
      metricProcessName,
      AMXL_ROUTE_INFO_METRIC_SVS,
      false,
    );
  } finally {
    publishCountToCSM({
      processName: METRIC_KEY,
      metricName: AMXL_ROUTE_INFO_METRIC_SVS,
    });
  }
}

async function getEnrichedAmxlRouteInformationBatch(
  routeIds,
  baseUrl,
  stationCode,
  stationConfig,
) {
  const startTime = Date.now();
  try {
    const api = axios.create({
      baseURL: baseUrl,
      timeout: TIMEOUTS.RSWS_DEFAULT_API_TIMEOUT,
    });
    const requestBody = {
      date: getDateInMMDDYYYYWithOffset(0),
      routeIds: routeIds,
    };
    var response;
    if (stationConfig && stationConfig.rswsbosonFlowEnabled) {
      const requestPayload = {
        resourcePath: "/apigw/stationCode/" + stationCode + "/routes-batch",
        httpMethod: "post",
        processName: "routeStaging",
        requestBody: requestBody,
      };
      response = await getAPIGatewayDataFromParent(requestPayload);
    } else {
      response = await api.post(
        `/rsws/stationCode/${stationCode}/routes-batch`,
        requestBody,
      );
    }
    logOperationalMetric(
      startTime,
      metricProcessName,
      AMXL_ROUTE_INFO_METRIC_RSWS,
      true,
    );
    return response.data;
  } catch (error) {
    logOperationalMetric(
      startTime,
      metricProcessName,
      AMXL_ROUTE_INFO_METRIC_RSWS,
      false,
    );
  } finally {
    publishCountToCSM({
      processName: METRIC_KEY,
      metricName: AMXL_ROUTE_INFO_METRIC_RSWS,
    });
  }
}

function getStowRouteAPIGatewayRequest(body) {
  return {
    resourcePath: "/svs/routes/data",
    httpMethod: "post",
    processName: "stow",
    requestBody: body,
  };
}

async function getRouteInformation(args) {
  const {
    dispatcher,
    filterDispatcher,
    baseUrl,
    capability,
    xptNodes,
    stationConfig,
    configDispatcher,
  } = args;
  const startTime = Date.now();
  try {
    const stationCode = getStationCode();
    const api = axios.create({ baseURL: baseUrl });
    var response;
    if (stationConfig && stationConfig.rswsbosonFlowEnabled) {
      const requestPayload = getRouteRequestPayload(
        capability,
        xptNodes,
        stationCode,
      );
      response = await getAPIGatewayDataFromParent(requestPayload);
    } else {
      response = await api.get(
        getRouteRequestURL(capability, xptNodes, stationCode),
      );
    }

    logOperationalMetric(startTime, metricProcessName, ROUTE_INFO_METRIC, true);
    publishCountToCSM({
      processName: METRIC_KEY,
      metricName: ROUTE_INFO_METRIC,
    });
    const output = response.data.routes;
    const routesNotAssignedCount = response.data.routesNotAssignedCount;
    const lastUpdated = new Date().getTime();
    dispatcher({
      type: "UPDATE_ROUTE_DATA_CACHE",
      data: {
        response: response.data,
        xptNodes: xptNodes,
        stationCode: stationCode,
      },
      lastUpdated: lastUpdated,
    });
    dispatcher({
      type: "RECEIVE_DATA",
      data: getRouteData(output, capability, stationConfig),
      routesNotAssignedCount: routesNotAssignedCount,
      lastUpdated: lastUpdated,
    });
    filterDispatcher({
      type: "RECEIVE_FILTER_DATA",
      data: getFilterData(output, stationConfig),
    });
    configDispatcher &&
      configDispatcher({
        type: "RECEIVE_REFRESH_INTERVAL",
        data: {
          refreshInterval: response.data.routeRefreshRate,
        },
      });
  } catch (error) {
    logOperationalMetric(
      startTime,
      metricProcessName,
      ROUTE_INFO_METRIC,
      false,
    );
    publishCountToCSM({
      processName: METRIC_KEY,
      metricName: ROUTE_INFO_METRIC,
    });
  }
}

function getRouteRequestURL(capability, xptNodes, stationCode) {
  const mainUri = `/rsws/route/station/${stationCode}?date=${getDateInMMDDYYYYWithOffset(
    0,
  )}`;
  if (
    DEFAULT_CAPABILITIES.includes(capability) &&
    Array.isArray(xptNodes) &&
    xptNodes.length
  ) {
    const nodeList = [...xptNodes];
    return `${mainUri}&nodes=${nodeList.join(",")}`;
  } else {
    return mainUri;
  }
}

function getRouteRequestPayload(capability, xptNodes, stationCode) {
  const mainUri = `/apigw/route/station/${stationCode}`;
  if (
    DEFAULT_CAPABILITIES.includes(capability) &&
    Array.isArray(xptNodes) &&
    xptNodes.length
  ) {
    const nodeList = [...xptNodes];
    const requestBody = {
      date: [getDateInMMDDYYYYWithOffset(0)],
      nodes: [nodeList.join(",")],
    };
    const requestPayload = {
      resourcePath: mainUri,
      httpMethod: "get",
      processName: "routeStaging",
      requestParams: requestBody,
    };
    return requestPayload;
  } else {
    const requestBody = {
      date: [getDateInMMDDYYYYWithOffset(0)],
    };
    const requestPayload = {
      resourcePath: mainUri,
      httpMethod: "get",
      processName: "routeStaging",
      requestParams: requestBody,
    };
    return requestPayload;
  }
}

export function getRouteData(output, capability, stationConfig) {
  const model = {
    routeList: [],
  };
  handleUnassignedRoutes(capability, output, stationConfig);
  output.forEach((route) => {
    try {
      route.criticalPullTime = getDispatchTime(route, capability);
      route.stagingLocations = transformStagingLocations(route);
      const routeDetail = {
        routeId: route.routeId,
        routeCode: route.routeCode,
        stationCode: route.stationCode,
        routeType: route.type,
        criticalPullTime: route.criticalPullTime,
        routeSPR: route.plannedPackagesCount,
        routeSLA: convertSecondsToHHMM(route.criticalPullTime),
        isPartial: isPartial(route, stationConfig, capability),
        pickProgress: getPickProgress(route, capability, stationConfig),
        rswsPickProgress: route.pickProgress,
        stageProgress: stationConfig.amxl
          ? NOT_APPLICABLE
          : route.stageProgress.completedCount +
            "/" +
            route.stageProgress.totalCount,
        departProgress:
          route.departProgress.completedCount +
          "/" +
          route.departProgress.totalCount,
        status: route.sidelined ? "Sidelined" : route.routeStatus,
        rswsStatus: route.routeStatus,
        overviewStatus: stationConfig.amxl
          ? route.routeStatus
          : getOverallRouteStatus(route, capability, stationConfig),
        picker: stationConfig.amxl
          ? NOT_APPLICABLE
          : formatAlias(getLatestAssignee(route)),
        pickDuration: stationConfig.amxl
          ? NOT_APPLICABLE
          : getPickTimeToBeDisplayed(route),
        stagingLocations: route.stagingLocations,
        stagingLocation: getRouteStagingLocation(route),
        hazmatWeight: route.hazmatWeight ? route.hazmatWeight : "",
        carts: route.carts,
        picklists: convertPicklistToUIModel(route, route.picklists),
        pickAssociates: route.pickAssociates,
        pickDurationInSeconds:
          calculatePickDurationWithMultipleAssociates(route),
        deliveryWindow: isValid(route.deliveryWindow)
          ? route.deliveryWindow
          : calculateDeliveryWindow(route),
        routeLength: calculateRouteLength(route),
        sourceNode: route.sourceNode, // Node from which route starts
      };
      model.routeList.push(routeDetail);
    } catch (e) {}
  });
  return model;
}

function getAssignmentEndTime(associate) {
  if (isTimeValid(associate.startTime) && !isTimeValid(associate.endTime)) {
    return new Date().getTime();
  } else {
    return associate.endTime;
  }
}

function calculatePickDurationWithMultipleAssociates(route) {
  if (route.pickAssociates !== undefined && route.pickAssociates.length > 0) {
    let startTimes = route.pickAssociates.map(
      (associate) => associate.startTime,
    );
    let endTimes = route.pickAssociates
      .map((associate) => getAssignmentEndTime(associate))
      .filter((e) => e);
    let minStartTime = Math.min(...startTimes);
    if (Array.isArray(endTimes) && endTimes.length > 0) {
      let maxEndTime = Math.max(...endTimes);
      return convertMillisToSecond(maxEndTime - minStartTime);
    } else {
      return getTimeDiffInSecondsFromCurrentTimeInHHMMSSFormat(minStartTime);
    }
  }
  return null;
}

function isRouteBelongToDefaultWindow(route) {
  if (
    route.departureWindowEndTime == null ||
    route.routeDuration == null ||
    route.maxRouteDuration == null
  ) {
    return true;
  }
  return false;
}

/** Return the Window of Route, for SSD only **/
/** Delivery Window is being calculated at RSWS.
 If we don't get the windows from RSWS only then this method will be used **/
export function calculateDeliveryWindow(route) {
  if (isRouteBelongToDefaultWindow(route)) {
    return SSDConstants.MISSING_INFORMATION;
  }
  //calculate  difference, in seconds,
  //between a date as evaluated in the UTC time zone, and the same date as evaluated in the local time zone.
  const offsetTimeZoneDifference = new Date().getTimezoneOffset() * 60;
  const departureWindowEndTime =
    route.departureWindowEndTime - offsetTimeZoneDifference;
  const promiseTime =
    (departureWindowEndTime % (24 * SSDConstants.HOURS)) + route.routeDuration;

  if (
    promiseTime > SSDConstants.BRUNCH_START &&
    promiseTime <= SSDConstants.BRUNCH_END
  ) {
    return SSDConstants.BRUNCH;
  } else if (
    promiseTime > SSDConstants.BRUNCH_END &&
    promiseTime <= SSDConstants.LUNCH_END
  ) {
    return SSDConstants.LUNCH;
  } else if (
    promiseTime > SSDConstants.LUNCH_END &&
    promiseTime <= SSDConstants.DINNER_END
  ) {
    return SSDConstants.DINNER;
  } else if (
    promiseTime > SSDConstants.DINNER_END &&
    promiseTime <= SSDConstants.BEDTIME_END
  ) {
    return SSDConstants.BEDTIME;
  } else {
    return SSDConstants.BREAKFAST;
  }
}

/** Return the length of Route in hours, for SSD only **/
function calculateRouteLength(route) {
  if (isRouteBelongToDefaultWindow(route)) {
    return "";
  }
  const length = Math.ceil(route.routeDuration / 1800);
  return length / 2;
}

function convertPicklistToUIModel(route, picklists) {
  if (Array.isArray(picklists)) {
    const result = [];
    picklists.forEach((picklist) => {
      const picklistModel = {
        routeId: route.routeId,
        routeCode: route.routeCode,
        stationCode: route.stationCode,
        picklistId: picklist.id,
        picklistCode: picklist.code,
        pickProgress:
          picklist.pickProgress.completedCount +
          "/" +
          picklist.pickProgress.totalCount,
        status: getPickStatusForPicklist(picklist),
        ovCount: picklist.itemsCount.ovCount,
        bagCount: picklist.itemsCount.bagCount,
      };
      result.push(picklistModel);
    });

    result.sort((a, b) =>
      a.picklistCode < b.picklistCode
        ? -1
        : a.picklistCode > b.picklistCode
          ? 1
          : 0,
    );

    return result;
  }
  return [];
}

/**
 * Return the assignee for AMZL sites
 * if route is not picked yet we use RPS information
 * if route is picked we use SPS information
 *
 * @param rswsRoute
 * @return {*|string}
 */
export function getLatestAssignee(rswsRoute) {
  if (rswsRoute.activeAssignee) {
    return rswsRoute.activeAssignee;
  } else if (rswsRoute.routeStatus === ASSIGNABLE) {
    return "...";
  } else {
    return Array.isArray(rswsRoute.pickAssociates) &&
      rswsRoute.pickAssociates.length
      ? rswsRoute.pickAssociates[0].alias
      : "...";
  }
}

function getPickProgress(rswsRoute, capability, stationConfig) {
  if (stationConfig.amxl) {
    if (rswsRoute.stowProgress) {
      return `${rswsRoute.stowProgress.completedCount} / ${rswsRoute.stowProgress.totalCount}`;
    } else {
      return `...`;
    }
  } else if (
    capability === AR_V2 ||
    stationConfig.pickStageDecoupleEnabled ||
    isARorSSD(capability)
  ) {
    return (
      rswsRoute.pickProgress.completedCount +
      "/" +
      rswsRoute.pickProgress.totalCount
    );
  } else {
    return (
      rswsRoute.pickProgressV2.completedCount +
      "/" +
      rswsRoute.pickProgressV2.totalCount
    );
  }
}

/**
 * return driver arrival time if available else fallback to RTW dispatch time.
 * @param route
 * @param capability
 * @return {*}
 */
function getDispatchTime(route, capability) {
  if (DEFAULT_CAPABILITIES.includes(capability) && route.plannedDispatchTime) {
    return route.plannedDispatchTime;
  }
  return route.criticalPullTime;
}

export function getFilterData(output, stationConfig) {
  const data = {
    departure: new Set(),
    driverType: stationConfig.amxl
      ? new Set([...Object.values(AMXL_ROUTE_TYPES)])
      : new Set(["DSP", "DP"]),
    cluster: new Set(),
    deliveryWindow: new Set(),
    shipmentType: new Set([...Object.values(AMXL_SHIPMENT_TYPES)]),
    sourceNode: new Set(),
  };
  output.forEach((route) => {
    if (route.type !== "...") {
      data.driverType.add(route.type);
    }
    data.cluster.add(getCluster(route.routeCode));
    if (route.criticalPullTime != null) {
      data.departure.add(convertSecondsToHHMM(route.criticalPullTime));
    }
    if (route.deliveryWindow !== null) {
      data.deliveryWindow.add(route.deliveryWindow);
    } else {
      data.deliveryWindow.add(calculateDeliveryWindow(route));
    }
    if (route.sourceNode !== null) {
      data.sourceNode.add(route.sourceNode);
    }
  });
  return data;
}

/** Get XPT Stations **/
export function fetchSubStations(
  dispatcher,
  baseUrl,
  capability,
  stationConfig,
) {
  if (DEFAULT_CAPABILITIES.includes(capability)) {
    getSubStationsWithParentNode(dispatcher, baseUrl, stationConfig);
  }
}

async function getSubStationsWithParentNode(
  dispatcher,
  baseUrl,
  stationConfig,
) {
  try {
    const api = axios.create({ baseURL: baseUrl });
    var response;
    if (stationConfig && stationConfig.rswsbosonFlowEnabled) {
      const requestPayload = {
        resourcePath: "/apigw/details/station/" + getStationCode(),
        httpMethod: "get",
        processName: "routeStaging",
      };
      response = await getAPIGatewayDataFromParent(requestPayload);
    } else {
      response = await api.get("/rsws/details/station/" + getStationCode());
    }
    const subStationsResponse = response.data;
    const xptNodeList = parseSubStationsResponse(subStationsResponse);
    if (
      subStationsResponse &&
      Array.isArray(xptNodeList) &&
      xptNodeList.length > 0
    ) {
      xptNodeList.push(getStationCode());
    }

    dispatcher({
      type: "RECEIVE_XPT_NODES",
      data: { xptNodes: xptNodeList },
    });
  } catch (e) {}
  return null;
}

function parseSubStationsResponse(response) {
  if (response && Array.isArray(response.subNodes)) {
    return response.subNodes.map((station) => {
      return station.code;
    }, []);
  }
  return [];
}

/** Get Station Config **/
export function fetchNodeConfig(dispatcher, baseUrl) {
  getNodeConfig(dispatcher, baseUrl);
}

async function getNodeConfig(dispatcher, baseUrl) {
  try {
    let response;
    if (await isRSWSBosonFlowEnabled()) {
      const requestPayload = {
        resourcePath: "/apigw/config",
        requestParams: { stationCode: [getStationCode()] },
        httpMethod: "get",
        processName: "routeStaging",
      };
      response = await getAPIGatewayDataFromParent(requestPayload);
    } else {
      const api = axios.create({ baseURL: baseUrl });
      response = await api.get("/rsws/config?stationCode=" + getStationCode());
    }
    const stationConfig = response.data;
    dispatcher({
      type: "RECEIVE_STATION_CONFIG",
      data: {
        stationConfig: {
          ...stationConfig,
          initialised: true,
          refreshInterval: stationConfig.routeRefreshRate,
        },
      },
    });
  } catch (e) {}
  return null;
}

/** Fetch DWP Metadata **/
export function getDWPInfo(dispatcher, baseUrl, stationConfig) {
  getDWPInfoFromRSWS(dispatcher, baseUrl, stationConfig);
}

async function getDWPInfoFromRSWS(dispatcher, baseUrl, stationConfig) {
  const startTime = Date.now();
  const stationCode = getStationCode();
  try {
    const api = axios.create({ baseURL: baseUrl });
    var response;
    if (stationConfig && stationConfig.rswsbosonFlowEnabled) {
      const requestPayload = {
        resourcePath: "/apigw/station/" + stationCode + "/dwpMetadata",
        httpMethod: "get",
        processName: "routeStaging",
      };
      response = await getAPIGatewayDataFromParent(requestPayload);
    } else {
      response = await api.get("/rsws/station/" + stationCode + "/dwpMetadata");
    }
    const output = response.data;
    logOperationalMetric(
      startTime,
      metricProcessName,
      DWP_METADATA_METRIC,
      true,
    );
    dispatcher({
      type: "RECEIVE_DWP_INFO",
      data: {
        pendingPlanFinalization: output.pendingPlanFinalization,
      },
    });
  } catch (error) {
    logOperationalMetric(
      startTime,
      metricProcessName,
      DWP_METADATA_METRIC,
      false,
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/outboundAmzl/network/Network.js